import { useState } from 'react';
import { downloadDocument } from '@apis/documents';
import { exportImageWithMarkup } from '../utils/exports/imageExport';
import { exportFailureOf, exportPdfWithMarkup, type ExportFailure } from '../utils/exports/pdfExport';
import { exportMimeFor, type MarkupItem } from '../utils/markupTypes';

interface IDownloadsInput {
  siteId: string;
  fileId: string;
  docName: string;
  isPdf: boolean;
  blob: Blob | null;
  /** Every checked mark, by page: a flattened PDF carries all of them, not just
   *  the page on screen. */
  itemsByPage: Record<number, MarkupItem[]>;
  page: number;
  /** Set when the caller named the file: the viewer already holds its bytes, so
   *  "Original file" saves those instead of fetching them from Genba again. */
  downloadName?: string;
}

/**
 * The two ways a document leaves the page: as the store holds it, or as a local
 * copy with the checked annotations flattened in. Neither writes anything back
 * — flattening happens in the browser, and the stored file is never touched.
 */
export function useAnnotationDownloads({
  siteId, fileId, docName, isPdf, blob, itemsByPage, page, downloadName,
}: IDownloadsInput) {
  const [downloading, setDownloading] = useState(false);
  // A flatten that cannot be written (an encrypted PDF) or bytes that fail to
  // fetch have to be reported: the browser shows nothing for either.
  const [failure, setFailure] = useState<ExportFailure | null>(null);

  async function withAnnotations() {
    // No bytes means the document never loaded: the marks have nothing to be
    // written into, and a silent no-op reads as a broken button.
    if (!blob) {
      setFailure('generic');
      return;
    }
    setDownloading(true);
    setFailure(null);
    try {
      const file = isPdf
        ? await exportPdfWithMarkup(blob, itemsByPage, docName)
        : await exportImageWithMarkup(blob, exportMimeFor(docName), itemsByPage[page] ?? [], docName);
      save(file);
    } catch (err) {
      setFailure(exportFailureOf(err));
    } finally {
      setDownloading(false);
    }
  }

  async function original() {
    setFailure(null);
    // The rendered document IS the original: saving the blob it was drawn from
    // costs no round trip. Only a cold deep link, which was handed no name, has
    // to ask the server — the server names it in that case.
    if (blob && downloadName) {
      save(new File([blob], downloadName));
      return;
    }
    setDownloading(true);
    try {
      await downloadDocument(siteId, fileId);
    } catch {
      setFailure('generic');
    } finally {
      setDownloading(false);
    }
  }

  return { downloading, failure, withAnnotations, original };
}

/** Hands a generated file to the browser. */
function save(file: File) {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  URL.revokeObjectURL(url);
}
